import dynStorage from '@/utils/storage-dynamic';

import firebase from 'firebase/app';

import { auth } from '@/utils/firebase.js';

import * as Sentry from '@sentry/vue';

import i18n from '@/i18n';
import { setOidcStepUpPending, clearOidcStepUpPending, clearOidcStepUp, readCustomTokenUid } from '@/helpers/sso/step-up';

const SSO_OIDC_PATH = '/sso/oidc';

// Static hosting 404s deep paths.
const appReturnUrl = () => `${ window.location.origin }/`;

// The MVNE wants +64XXXXXXXXX.
const NZ_MOBILE_FORMAT = /^\+64[2-9]\d{7,9}$/;

const toNzMobile = (value) => {
	if (!value) return '';

	let number = String(value).replace(/[^\d+]/g, '');

	if (number.startsWith('0')) number = `+64${ number.slice(1) }`;
	else if (number.startsWith('64')) number = `+${ number }`;

	if (NZ_MOBILE_FORMAT.test(number)) return number;

	return '';
};

// Profile from the /exchange response.
const readOidcIdentity = (payload = {}) => {
	let sso_data = payload.SSOData;

	if (typeof sso_data === 'string') {
		try { sso_data = JSON.parse(sso_data); } catch (error) { sso_data = null; }
	}

	const data = sso_data || {};

	return {
		email: data.Email || '',
		mobile_number: toNzMobile(data.Phone),
		first_name: data.FirstName || '',
		last_name: data.LastName || '',
		title: data.Title || '',
		// Air NZ customer GUID.
		external_customer_id: data.ExternalCustomerID ? String(data.ExternalCustomerID).trim() : '',
		membership_id: data.MembershipID ? String(data.MembershipID) : '',
		// The MVNE field is a string.
		loyalty_member: data.LoyaltyMember == null ? '' : String(data.LoyaltyMember),
		profile: sso_data || null,
	};
};

const displayName = (creds) => {
	if (!creds) return '';
	if (!creds.displayName || !creds.displayName.trim()) return creds.email || '';

	const [first_name, last_name] = creds.displayName.split('  ').filter((x) => x);
 
	if (!last_name || (first_name.length + last_name.length > 33)) return first_name;

	return `${first_name} ${last_name}`;
};

export default {
  state: {
    autologin: Promise.resolve(),
    processing_autologin: null,
    auth_user: null,
    auth_token: null,
    // Captured SSO profile/tokens for a user with no existing account, held for the
    // downstream signup flow. Restored from sessionStorage so it survives a page reload.
    sso_signup_data: (() => {
      try {
        return JSON.parse(sessionStorage.getItem('sso-signup-data')) || null;
      } catch (e) {
        return null;
      }
    })(),
  },

  getters: {
    is_processing_autologin: (state) => Boolean(state.processing_autologin),
    is_processing_preload: (state) => Boolean(state.processing_autologin),

    is_auth: (state) => Boolean(state.auth_user),

    auth_user: (state) => state.auth_user,
    auth_token: (state) => state.auth_token,

    auth_user_uid: (state) => (state.auth_user && state.auth_user.uid) || '',
    auth_user_has_password: (state) => state.auth_user && state.auth_user.providerData?.[0]?.providerId == 'password',
    is_auth_user_social_logged_in: (state, getters) => getters.is_auth && !getters.auth_user_has_password,
    is_oidc_sso_user: (state) => Boolean(state.auth_user) && Boolean(dynStorage.get('oidc-session-slug', false)),

    // '' = not SSO, else 'google' | 'apple' | brand slug (e.g. 'airnz').
    auth_user_sso_provider: (state) => {
      if (!state.auth_user) return '';

      const provider_ids = (state.auth_user.providerData || []).map((provider) => provider?.providerId || '');

      if (provider_ids.includes('password')) return '';
      if (provider_ids.includes('google.com')) return 'google';
      if (provider_ids.includes('apple.com')) return 'apple';

      const federated_id = provider_ids.find((id) => /^(oidc|saml)\./.test(id));

      if (federated_id) return federated_id.replace(/^(oidc|saml)\./, '');

      // Brand SSO uses a custom token (no provider), so rely on its session marker.
      // Other custom-token logins (e.g. mobile onboarding) have no marker.
      return dynStorage.get('oidc-session-slug', false) || '';
    },
    is_auth_user_sso: (state, getters) => Boolean(getters.auth_user_sso_provider),
    // LPD-3846: SSO users manage their details on the SSO side.
    is_sso_account_details_locked: (state, getters) => getters.sso_hide_account_details && getters.is_auth_user_sso,

	  auth_user_first_name: (state) => {
      const [firstName = ''] =  (state.auth_user?.displayName ?? '').split('  ')
      return firstName
    },
    auth_user_last_name: (state) => {
      const [_, lastName = ''] =  (state.auth_user?.displayName ?? '').split('  ')
      return lastName
    },
    auth_user_email: (state) => state.auth_user?.email,
    
    auth_display_name: (state) => (state.auth_user?.displayName ?? '').replace('  ', ' '),
	auth_user_avatar: (state) => state.auth_user?.photoURL,

	auth_user_profile_creds: (state, getters) => ({
		first_name: getters.account_first_name || getters.auth_user_first_name,
		last_name: getters.account_last_name || getters.auth_user_last_name,
		// Brand SSO signs in with a custom token, which leaves the Firebase user without an
		// email, so fall back to the customer record.
		email: getters.auth_user_email || getters.account_email,
		customer_email: getters.account_email,
		phone: getters.account_phone,
		external_ref5: getters.account_external_ref5,
        displayName: `${getters.account_first_name || getters.auth_user_first_name}  ${getters.account_last_name || getters.auth_user_last_name}`
	}),
    auth_user_display_name: (state, getters) => displayName(getters.auth_user_profile_creds),

    sso_signup_data: (state) => state.sso_signup_data,
  },

  actions: {
	  updateAuthUserProfile({ getters, dispatch, commit }, { FirstName, LastName, ExternalRef5, MobileNumber, CustomerEmail, update_MVNE_customer = true }) {
	    const current_user = auth().currentUser;
        const displayName = `${FirstName}  ${LastName}`;
        const Email = CustomerEmail || getters.auth_user_profile_creds.customer_email;

		return current_user.updateProfile({ displayName })
        .then(() => {
		    commit('SAVE_AUTH_USER', auth().currentUser);
            // No return
            if (update_MVNE_customer) {
                dispatch('UpdateMVNECustomer', { FirstName, LastName, ExternalRef5, Email, MobileNumber });
            }
		})
	},

    CheckAuthUserPassword({}, pass) {
      const current_user = auth().currentUser;
		  const current_user_email = auth().currentUser.email;
  
		  const credential = auth.EmailAuthProvider.credential(current_user_email, pass);

      return current_user.reauthenticateWithCredential(credential).catch((error) => {
		    if (error.code === 'auth/wrong-password') {
                return Promise.reject(`${i18n.t('the_password_is_invalid')}, ${i18n.t('please_enter_correct_one')}`)
		    } else {
                return Promise.reject(error.message)
		    }
	  	});
    },

	ChangeAuthUserPassword({}, { pass, passnew }) {
		const current_user = auth().currentUser;
		const current_user_email = auth().currentUser.email;
  
		const credential = auth.EmailAuthProvider.credential(current_user_email, pass);
  
		return current_user
		  .reauthenticateWithCredential(credential)
		  .then(() => {
			return current_user.updatePassword(passnew);
		  })
		  .catch((error) => {
			if (error.code === 'auth/wrong-password') {
			  throw new Error(`${i18n.t('old_password_is_invalid')}, ${i18n.t('please_enter_correct_one')}`);
			} else {
			  throw new Error(error.message);
			}
		  });
	},

	ChangeAuthUserEmail({ getters, dispatch, commit }, { pass, email }) {
		const current_user = auth().currentUser;
		const current_user_email = auth().currentUser.email;
  
		const credential = auth.EmailAuthProvider.credential(current_user_email, pass);

        // Don't update MVNE Customer when Auth User Email is changed
        // const FirstName = getters.auth_user_profile_creds.first_name;
        // const LastName = getters.auth_user_profile_creds.last_name;
        // const ExternalRef5 = getters.auth_user_profile_creds.external_ref5;
        // const MobileNumber = getters.auth_user_profile_creds.phone;
  
		return current_user
		  .reauthenticateWithCredential(credential)
		  .then(() => {
			return current_user.updateEmail(email)
			.then(() => {
				commit('SAVE_AUTH_USER', auth().currentUser);

                // No return
                // dispatch('UpdateMVNECustomer', { FirstName, LastName, ExternalRef5, Email: email, MobileNumber });
			});
		  })
		  .catch((error) => {
			if (error.code === 'auth/wrong-password') {
			  throw new Error(`${i18n.t('the_password_is_invalid')}, ${i18n.t('please_enter_correct_one')}`);
			} else {
			  throw new Error(error.message);
			}
		  });
	},

	ForgotPassword({}, email) {
		return firebase.auth().sendPasswordResetEmail(email);
	},

    FirebaseSignUp({ getters, dispatch }, user_data) {
      return firebase
        .auth()
        .createUserWithEmailAndPassword(user_data.email, user_data.passnew)
        .then((userCredential) => {
		  // Signed in
          dispatch('logAnalyticsEventSignUp', { parameters: { method: "password" }})

		  const user = userCredential.user;
          const uid = user.uid;

          const displayName = (user_data.firstname && user_data.lastname) ? `${user_data.firstname.trim()}  ${user_data.lastname.trim()}` : ''

		  return user.updateProfile(
			  	{ displayName }
			  ).then(() => {
				return dispatch('GetAccountByUid', uid).then((account)=> {
					if (account) {
						return dispatch('FirebaseOnAuth');
					} else {
					  return dispatch('CreateAccount', { user_data: {}, uid }).then(()=> {
						return dispatch('FirebaseOnAuth');
					  });
					}
				  });
			  })
        })
        .catch((error) => {
          return Promise.reject(error);
        });
    },

    FirebaseSignInEmailPassword({ getters, dispatch }, { email, login, pass }) {
      const sign_in_email = email || login

      return firebase
        .auth()
        .signInWithEmailAndPassword(sign_in_email, pass)
        .then(() => {
          // Signed in
          dispatch('logAnalyticsEventLogin', { parameters: { method: "password" }});

          return dispatch('FirebaseOnAuth');
        })
        .catch((error) => {
          return Promise.reject(error);
        });
    },

    FirebaseSignInWithCustomToken({ getters, dispatch }, token) {
      return firebase
        .auth()
        .signInWithCustomToken(token)
        .then(() => {
          // Signed in
          console.log('FirebaseSignInWithCustomToken Signed in');

          dispatch('logAnalyticsEventLogin', { parameters: { method: "token" }});

          return dispatch('FirebaseOnAuth');
        })
        .catch((error) => {
          console.log('FirebaseSignInWithCustomToken error', error);

          return Promise.reject(error);
        });
    },

	FirebaseGoogleAuth({ dispatch }) {
      console.info('Google sign in');
      const provider = new firebase.auth.GoogleAuthProvider();

	  provider.setCustomParameters({
		prompt: 'select_account',
	  });

	  firebase.auth().languageCode = 'en';

      return firebase
        .auth()
        .signInWithPopup(provider)
        .then((result) => dispatch('HandleSsoAuthResult', { result, method: 'google' }))
        .catch((error) => {
          console.error(error);
          return Promise.reject(error);
        });
    },

    FirebaseAppleAuth({ dispatch }) {
      console.info('Apple sign in');
      const provider = new firebase.auth.OAuthProvider('apple.com');

	  provider.setCustomParameters({
		prompt: 'select_account',
	  });

	  firebase.auth().languageCode = 'en';

      return firebase
        .auth()
        .signInWithPopup(provider)
        .then((result) => dispatch('HandleSsoAuthResult', { result, method: 'apple' }))
        .catch((error) => {
          console.error(error);
          return Promise.reject(error);
        });
    },

    // Brand SSO: full-page redirect.
    // When step_up + action_key are provided, the same flow is reused for
    // re-authentication: the URL carries step_up=true (the backend's
    // StepUpAuthParams translates that into provider-specific params) and an
    // intent flag is written to sessionStorage instead of the sign-in marker.
    OidcSignIn({ getters }, { step_up, action_key } = {}) {
      const slug = getters.oidc_sso_slug;

      if (!getters.oidc_origin || !slug) return Promise.reject(Error(i18n.t('error_occurred')));
      if (step_up && !action_key) return Promise.reject(Error(i18n.t('error_occurred')));

      // Land here after the IdP redirects back.
      sessionStorage.setItem('oidc_return_to', `${ window.location.pathname }${ window.location.search }`);

      if (step_up) {
        sessionStorage.removeItem('oidc_pending');
        setOidcStepUpPending(action_key);
      } else {
        sessionStorage.removeItem('oidc_logging_out');
        clearOidcStepUpPending();
        sessionStorage.setItem('oidc_pending', '1');
      }

      const query = step_up
        ? `?step_up=true&return_to=${ encodeURIComponent(appReturnUrl()) }`
        : `?return_to=${ encodeURIComponent(appReturnUrl()) }`;

      const start_url = `${ getters.oidc_origin }${ SSO_OIDC_PATH }/${ slug }/start${ query }`;

      window.location.assign(start_url);

      // Keeps the caller's spinner up.
      return new Promise(() => {});
    },

    // Exchanges the OIDC authorization code.
    // For sign-in (step_up falsy): signs in with the custom token and boots the session.
    // For step-up (step_up truthy): verifies the token UID matches the current user, then signs in
    // with it so later requests carry the step_up claim the gated endpoints require.
    OidcExchange({ dispatch, getters, commit }, { code, step_up }) {
      const slug = getters.oidc_sso_slug;

      if (!getters.oidc_origin || !slug) return Promise.reject(Error(i18n.t('error_occurred')));

      const exchange_url = `${ getters.oidc_origin }${ SSO_OIDC_PATH }/${ slug }/exchange`;

      let identity = null;

      return fetch(exchange_url, {
        method: 'POST',
        credentials: 'omit',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      })
        .then((response) => {
          if (!response.ok) return Promise.reject(Error(`OIDC exchange failed (${ response.status })`));

          return response.json().catch(() => Promise.reject(Error('OIDC exchange returned invalid JSON')));
        })
        .then((payload) => {
          const { CustomToken } = payload || {};

          if (!CustomToken) return Promise.reject(Error('OIDC exchange returned no CustomToken'));

          if (step_up) {
            return new Promise((resolve) => {
              const unsubscribe = firebase.auth().onAuthStateChanged((user) => {
                unsubscribe();
                resolve(user);
              });
            }).then((user) => {
              if (!user || readCustomTokenUid(CustomToken) !== user.uid) return Promise.reject(Error('OIDC step-up user mismatch'));

              return firebase.auth().signInWithCustomToken(CustomToken)
                // Keeps the stored token in sync; never fails the step-up.
                .then(({ user: stepped_up_user }) => stepped_up_user.getIdToken()
                  .then((token) => commit('SAVE_AUTH_TOKEN', token))
                  .catch(() => {}));
            });
          }

          identity = readOidcIdentity(payload);

          return firebase.auth().signInWithCustomToken(CustomToken);
        })
        .then((result) => {
          if (step_up) return;

          dynStorage.set('oidc-session-slug', slug, null, false);

          return dispatch('HandleSsoAuthResult', { result, method: 'brand', identity });
        });
    },

    // Shared post-popup handler for every SSO provider (Google / Apple / brand).
    // 1. Normalizes the Firebase displayName to the app's two-space "first  last" convention.
    // 2. Recognizes the "no account found" case (no Firestore access doc for this uid) and,
    //    before continuing, captures the SSO user profile + tokens into session state so the
    //    downstream signup flow can consume it.
    // `identity` is OIDC only.
    HandleSsoAuthResult({ dispatch, getters }, { result, method, identity }) {
      const user = result.user;
      const uid = user.uid;

      // Normalize the display name to the app convention (first + two spaces + last).
      // A custom-token record can arrive with no displayName at all, so fall back to the
      // names the IdP sent us. Google/Apple always supply one, so nothing changes for them.
      const ensure_display_name = () => {
        const current = (user?.displayName || '').trim();

        const from_identity = [identity?.first_name, identity?.last_name]
          .map((part) => (part || '').trim())
          .filter(Boolean)
          .join('  ');

        const target = current
          ? current.replace(/ {2,}/g, ' ').replace(' ', '  ')
          : from_identity;

        if (!target || target === current) return Promise.resolve();

        return user.updateProfile({ displayName: target });
      };

      // Saves the profile on `users/{uid}`.
      const save_sso_profile = () => {
        if (!identity?.profile) return Promise.resolve();

        const data = { SsoProvider: dynStorage.get('oidc-session-slug', false) || method };

        // Never blank a saved value.
        if (identity.email) data.Email = identity.email;
        if (identity.first_name) data.FirstName = identity.first_name;
        if (identity.last_name) data.LastName = identity.last_name;
        if (identity.title) data.Title = identity.title;

        // ExternalCustomerID feeds ExternalRef4.
        if (identity.external_customer_id) data.ExternalCustomerID = identity.external_customer_id;

        // ExternalID feeds ExternalRef5.
        if (identity.membership_id) data.ExternalID = identity.membership_id;

        // LoyaltyMember feeds ExternalRef3.
        if (identity.loyalty_member) data.LoyaltyMember = identity.loyalty_member;

        return dispatch('mergeMockupUser', { data, uid }).catch(() => {});
      };

      // Keeps an existing customer's Koru fields current.
      const sync_customer_refs = () => {
        if (!identity) return Promise.resolve();

        const uuid = getters.current_account_uid
          || (getters.account_customers_uuids?.length === 1 ? getters.account_customers_uuids[0] : '');

        if (!uuid) return Promise.resolve();

        // Never block sign-in on a refs update.
        return dispatch('SyncSsoCustomerRefs', { UUID: uuid, identity }).catch(() => {});
      };

      // A failure here only shows a generic message, and FirebaseOnAuth's own catch
      // logs the user out and wipes sso_signup_data, so record where it broke.
      let step = 'start';

      return ensure_display_name()
        .then(() => {
          step = 'get-account';

          return dispatch('GetAccountByUid', uid);
        })
        .then((account) => {
          if (!account) return null;

          // The marker doc is written before the create-account modal, so an abandoned or
          // failed signup leaves one behind with no customer bound. Treat that as no account,
          // else the user is stuck as an account holder with nothing to show and can never
          // reach the create journey again.
          step = 'check-account-customers';

          return dispatch('GetCustomersUuidsByUserUid', uid)
            .then((uuids) => (uuids?.length ? account : null))
            .catch(() => account);
        })
        .then((account) => {
          // Account exists = Firestore doc /access/FIREBASE/users/{uid} present (keyed on uid).
          if (account) {
            dispatch('logAnalyticsEventLogin', { parameters: { method } });

            // Refresh an older account too.
            return save_sso_profile()
              .then(() => {
                step = 'existing-account-on-auth';

                return dispatch('FirebaseOnAuth');
              })
              .then((on_auth) => sync_customer_refs().then(() => on_auth));
          }

          dispatch('logAnalyticsEventSignUp', { parameters: { method } });

          // LPD-3702: read the post-signup landing gate before continuing so the view routes on a
          // settled value. Cap the wait and swallow errors so a slow/failed fetch can't hang login.
          const gate_ready = Promise.race([
            Promise.resolve().then(() => dispatch('get_sso_signup_redirect_to_dashboard')).catch(() => {}),
            new Promise((resolve) => setTimeout(resolve, 1500)),
          ]);

          return gate_ready
            .then(() => {
              step = 'capture-signup-data';

              return dispatch('CaptureSsoSignupData', { result, method, identity });
            })
            .then(() => {
              step = 'create-account-marker';

              return dispatch('CreateAccount', { user_data: {}, uid });
            })
            .then(() => save_sso_profile())
            .then(() => {
              step = 'no-account-on-auth';

              return dispatch('FirebaseOnAuth');
            });
        })
        .catch((error) => {
          Sentry.captureException(error instanceof Error ? error : Error(String(error?.message || error)), {
            tags: { flow: 'sso-sign-in', method, step },
            extra: { uid, code: error?.code },
          });

          return Promise.reject(error);
        });
    },

    // MembershipID is the Koru number, LoyaltyMember a string flag, ExternalCustomerID the GUID.
    SyncSsoCustomerRefs({ dispatch, getters }, { UUID, SPID, identity } = {}) {
      const source = identity || getters.sso_signup_data;
      const uuid = UUID || getters.current_account_uid;

      if (!uuid || !source) return Promise.resolve();

      const payload = {};

      if (source.membership_id && source.membership_id !== getters.account_external_ref5) {
        payload.ExternalRef5 = String(source.membership_id);
      }

      if (source.loyalty_member && source.loyalty_member !== (getters.current_account?.ExternalRef3 || '')) {
        payload.ExternalRef3 = String(source.loyalty_member);
      }

      if (source.external_customer_id && source.external_customer_id !== (getters.current_account?.ExternalRef4 || '')) {
        payload.ExternalRef4 = String(source.external_customer_id);
      }

      if (!Object.keys(payload).length) return Promise.resolve();

      return dispatch('api_customer/SetCustomerFields', { payload, SPID: SPID || getters.app_spid, UUID: uuid });
    },

    // Captures the SSO user profile + tokens returned by signInWithPopup and stores them in
    // session state (Vuex + sessionStorage 'sso-signup-data') for the downstream signup flow.
    // Provider-agnostic: works for Google, Apple and any brand OIDC/SAML IdP.
    CaptureSsoSignupData({ commit }, { result, method, identity }) {
      const user = result.user;
      // OIDC has no popup profile.
      const profile = (result.additionalUserInfo && result.additionalUserInfo.profile) || identity?.profile || {};
      const [display_first = '', display_last = ''] = (user.displayName || '').split('  ');

      const sso_signup_data = {
        uid: user.uid,
        provider: method,                                          // 'google' | 'apple' | 'brand'
        provider_id: user.providerData?.[0]?.providerId || null,   // 'google.com' | 'apple.com' | 'oidc.*'
        email: user.email || profile.email || identity?.email || '',
        mobile_number: identity?.mobile_number || '',      // OIDC only
        title: identity?.title || '',                     // OIDC only
        external_customer_id: identity?.external_customer_id || '', // OIDC only, feeds ExternalRef4
        membership_id: identity?.membership_id || '',      // OIDC only, feeds ExternalRef5
        loyalty_member: identity?.loyalty_member || '',    // OIDC only, feeds ExternalRef3
        email_verified: Boolean(user.emailVerified),
        // Claims from each provider.
        first_name: profile.given_name || profile.first_name || profile.name?.firstName || identity?.first_name || display_first,
        last_name: profile.family_name || profile.last_name || profile.name?.lastName || identity?.last_name || display_last,
        display_name: user.displayName || '',
        photo_url: user.photoURL || '',
        provider_id_token: (result.credential && result.credential.idToken) || null,
        firebase_id_token: null,
        // Everything we don't map yet.
        provider_profile: profile,
      };

      return user
        .getIdToken()
        .then((firebase_id_token) => {
          sso_signup_data.firebase_id_token = firebase_id_token;
        })
        .catch((error) => {
          console.error('CaptureSsoSignupData getIdToken error', error);
        })
        .then(() => {

          commit('SET_SSO_SIGNUP_DATA', sso_signup_data);

          return sso_signup_data;
        });
    },

    FirebaseGetUpdatedAuthToken({ commit }) {
        const user = firebase.auth().currentUser;

        if (user) {
            return user.getIdToken().then(token => {
      		  commit('SAVE_AUTH_TOKEN', token);

              return Promise.resolve(token);
      		}).catch(error => {
                console.log('FirebaseGetUpdatedAuthToken Error', error);

                return Promise.resolve(null);
            })
        }

        return Promise.resolve(null);
    },

    FirebaseOnAuth({ getters, dispatch, commit }, { firebase_auto_login } = {}) {
      const current_auth_user_uid = dynStorage.get('current-auth-user-uid', false);

	  const auth_state_changed = new Promise((resolve, reject) => {
        const unsubscribe = firebase.auth().onAuthStateChanged((user) => {
            if (!user) {
                unsubscribe();

                dispatch('FirebaseLogout')
            } else if (current_auth_user_uid && user.uid !== current_auth_user_uid) {
                unsubscribe();

                reject();
            }

            resolve(user);
        });
      });

	  return auth_state_changed
        .then((user) => {
            console.info('User is signed in', user);

	  		if (!user) return Promise.resolve();

            dynStorage.set('current-auth-user-uid', user.uid, null, false);

      		user.getIdToken().then((token) => {
      		  commit('SAVE_AUTH_TOKEN', token);
      		});

            if (user.photoURL) {
                commit('SAVE_AUTH_USER', user);
            } else {
                commit('SAVE_AUTH_USER', {
                    ...user,
                    photoURL: user.providerData?.[0]?.photoURL,
                });
            }

	  		const auth_user_uid = getters.auth_user_uid;
        // console.info('auth_user_uid', auth_user_uid);

      		return dispatch('GetCustomersUuidsByUserUid', auth_user_uid);
	  	})
		.then(() => {
			return dispatch('mockupCustomer');
		})
		.catch((error) => {
			dispatch('FirebaseLogout');

			return Promise.reject(error);
		});
    },

    FirebaseLogout({ getters, dispatch, commit }) {

        const oidc_slug = dynStorage.get('oidc-session-slug', false);
        if (oidc_slug) dynStorage.remove('oidc-session-slug');
        clearOidcStepUp();

        dispatch('Logout');

        if (getters.is_account) {
            dispatch('UnbindCurrentAccount').then(() => {
                dispatch('UnbindBindBillingRoutes');
                dispatch('UnbindCustomerAllProducts');
                dispatch('UnbindCurrentProductGetUsageTrigger');

                if (getters.is_account_postpay) {
                    dispatch('UnbindPostpayProductBalance');
                }
            })
        }

        commit('SAVE_CURRENT_ACCOUNT_UID', null);
        commit('SET_CUSTOMER_CURRENT_PRODUCT', null);
        commit('SET_CUSTOMER_CURRENT_MOBILE_PRODUCT_UUID', '');
        commit('SET_IS_CUSTOMER_CURRENT_ADDONS_PAYG', false);
        commit('DELETE_INVOICES');
        commit('resetInvoices_v3')
        commit('RESET_PAYMENT_HISTORY')
        commit('saveMockupUser', {})
        commit('SET_IS_MOBILE_ONBOARDING', false);
        commit('eshopCartReset_v2');
        commit('eshopPurchasedReset_v2');
        commit('resetDeliveryAddresses');
        commit('RESET_CUSTOMER_GROUPS_AND_MEMBERS');
        commit('RESET_MOBILE_GROUPS_AND_MEMBERS');
        commit('RESET_CUSTOMER_INVITES');
        commit('RESET_MOBILE_INVITES');
        commit('RESET_USAGE_CHARGES');
        commit('RESET_SIM_SETTINGS')
        commit('RESET_SIM_SETTINGS_ROAMING')
        commit('SET_NO_ADDONS_NOTIFIED', false)

        if (getters.is_app_support_service_gleap) {
            dispatch('LogOutGleapUser');
        } else if (getters.is_app_support_service_intercom) {
            commit('LOGOUT_INTERCOM_CHAT');
        } else {
            commit('LOGOUT_ZENDESK_CHAT');
        }

        commit('eshopCheckoutFlowsReset_v2')

        dispatch('GetCatalogServices');

    	return firebase
          .auth()
          .signOut()
          .then(() => {
			commit('FIREBASE_LOGOUT');

			// Drop the provider's session too.
			if (oidc_slug && getters.oidc_origin) {
				const logout_url = `${ getters.oidc_origin }${ SSO_OIDC_PATH }/${ oidc_slug }/logout`
					+ `?return_to=${ encodeURIComponent(appReturnUrl()) }`;

				// Router spots the landing.
				sessionStorage.setItem('oidc_logging_out', '1');

				window.location.assign(logout_url);
			}
		  });
    },

    FirebaseAutoLogin({ dispatch, commit, getters }) {
      commit('SAVE_PROCESSING_AUTOLOGIN', true);

      dispatch('BindGetCatalogServicesTrigger');
      dispatch('BindGetCatalogServicesSldTrigger');

      const auth_state_changed = new Promise((resolve) => {
        const unsubscribe = firebase.auth().onAuthStateChanged((user) => {
          unsubscribe();
          resolve(user);
        });
      });

      const autologin = auth_state_changed
        .then((user) => {
          dispatch('QuickReloadSupport');

          if (user) {
            dispatch('get_is_hide_prepay_topup');
            dispatch('get_is_module_ufb_enabled');

	        if (getters.current_account_uid) {
		        dispatch('BindCurrentAccount', getters.current_account_uid);
		        dispatch('BindCustomerAllProducts', getters.current_account_uid);

                if (getters.is_app_environment_billing_routing_allowed) {
                    dispatch('BindBillingRoutes', getters.current_account_uid);
                }
	        }
            
            return dispatch('FirebaseOnAuth', { firebase_auto_login: true });
          } else {
            return Promise.reject();
          }
        })
        .then(() => {})
        .catch(() => {
          dispatch('FirebaseLogout');
        })
        .finally(() => commit('SAVE_PROCESSING_AUTOLOGIN', false));

      commit('SAVE_AUTOLOGIN', autologin);

	  return autologin;
    },

    FirebaseUserDelete() {
      const current_user = auth().currentUser;

      return current_user.delete()
    },
  },

  mutations: {
    SAVE_PROCESSING_AUTOLOGIN(state, value) {
      state.processing_autologin = value;
    },

    SAVE_AUTOLOGIN(state, autologin) {
      state.autologin = autologin;
    },

    SAVE_AUTH_USER(state, user) {
        state.auth_user = {...user };
    },

    SAVE_AUTH_TOKEN(state, token) {
        if (state.auth_token != token) {
            state.auth_token = token;
        }
    },

    SET_SSO_SIGNUP_DATA(state, sso_signup_data) {
      state.sso_signup_data = sso_signup_data;

      if (sso_signup_data) {
        sessionStorage.setItem('sso-signup-data', JSON.stringify(sso_signup_data));
      } else {
        sessionStorage.removeItem('sso-signup-data');
      }
    },

    FIREBASE_LOGOUT(state) {
      state.auth_user = null;
      state.auth_token = null;
      state.sso_signup_data = null;
      sessionStorage.removeItem('sso-signup-data');
    },
  },
};
